import { useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Modal } from "../components/Modal";
import { ProgressRing } from "../components/ProgressRing";
import { StatusBadge } from "../components/StatusBadge";
import { useWorkspace } from "../features/workspace-context";
import { calculateWeightedProgress } from "../lib/progress";
import { slugify } from "../lib/format";
import type { CreateProjectInput } from "../types/domain";

export function ProjectPortfolioPage() {
  const { data, createProject, bootstrapEremosWorkspace, mode } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<CreateProjectInput>({
    name: "",
    slug: "",
    summary: "",
    vision: "",
    accent: "#22d3c5",
  });

  const summaries = useMemo(
    () =>
      (data?.projects ?? []).map((project) => {
        const items = data?.workItems.filter((item) => item.projectId === project.id) ?? [];
        return {
          project,
          items,
          progress: calculateWeightedProgress(items),
          focus: items.find((item) => item.isCurrentFocus),
        };
      }),
    [data],
  );

  if (!data) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await createProject({ ...form, slug: form.slug || slugify(form.name) });
      setOpen(false);
      setForm({ name: "", slug: "", summary: "", vision: "", accent: "#22d3c5" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-stack">
      <section className="hero-panel">
        <div>
          <span className="eyebrow">EremosHub portfolio</span>
          <h1>Una vista honesta del trabajo real.</h1>
          <p>
            El dashboard diferencia lo aceptado, lo validado, lo que vive solo en una rama y lo que
            todavía es visión. Ningún porcentaje sustituye la evidencia técnica.
          </p>
        </div>
        <div className="hero-panel__actions">
          <button className="button button--primary" type="button" onClick={() => setOpen(true)}>
            Nuevo proyecto
          </button>
          {data.projects.length === 0 && (
            <button
              className="button button--secondary"
              type="button"
              onClick={() => void bootstrapEremosWorkspace()}
            >
              Cargar roadmap Eremos
            </button>
          )}
        </div>
      </section>

      {mode === "demo" && (
        <div className="notice notice--info">
          Estás explorando una copia local editable. Con Supabase configurado, los mismos flujos
          quedan protegidos por Auth y RLS.
        </div>
      )}

      <section className="project-grid">
        {summaries.map(({ project, progress, focus, items }) => {
          const accepted = items.filter((item) => item.status === "accepted").length;
          return (
            <Link
              key={project.id}
              className="project-card"
              to={`/projects/${project.id}/overview`}
              style={{ "--project-accent": project.accent } as CSSProperties}
            >
              <div className="project-card__header">
                <span className="project-card__code">{project.slug.toUpperCase()}</span>
                <span className={`project-state project-state--${project.status}`}>
                  {project.status === "active"
                    ? "Activo"
                    : project.status === "paused"
                      ? "Pausado"
                      : "Archivado"}
                </span>
              </div>
              <div className="project-card__body">
                <div>
                  <h2>{project.name}</h2>
                  <p>{project.summary}</p>
                </div>
                <ProgressRing value={progress.percent} label="roadmap conocido" />
              </div>
              <div className="project-card__focus">
                <span className="eyebrow">Foco actual</span>
                {focus ? (
                  <>
                    <strong>{focus.title}</strong>
                    <StatusBadge status={focus.status} compact />
                  </>
                ) : (
                  <span className="muted">Sin foco marcado</span>
                )}
              </div>
              <footer className="project-card__footer">
                <span>{accepted} aceptados</span>
                <span>{progress.consideredItems} hojas ponderadas</span>
                <span>Ver proyecto →</span>
              </footer>
            </Link>
          );
        })}
      </section>

      <section className="principles-strip">
        <div>
          <span>01</span>
          <strong>Fuente de verdad separada</strong>
          <p>GitHub demuestra código. El dashboard enlaza y explica.</p>
        </div>
        <div>
          <span>02</span>
          <strong>Progreso versionado</strong>
          <p>Pesos, factores y snapshots conservan el contexto del porcentaje.</p>
        </div>
        <div>
          <span>03</span>
          <strong>Seguridad desde la base</strong>
          <p>La interfaz ayuda; RLS autoriza.</p>
        </div>
      </section>

      <Modal open={open} title="Crear proyecto" onClose={() => setOpen(false)}>
        <form className="form-stack" onSubmit={(event) => void submit(event)}>
          <label>
            Nombre
            <input
              required
              value={form.name}
              onChange={(event) =>
                setForm({ ...form, name: event.target.value, slug: slugify(event.target.value) })
              }
            />
          </label>
          <label>
            Slug
            <input
              required
              value={form.slug}
              onChange={(event) => setForm({ ...form, slug: slugify(event.target.value) })}
            />
          </label>
          <label>
            Resumen
            <textarea
              required
              rows={3}
              value={form.summary}
              onChange={(event) => setForm({ ...form, summary: event.target.value })}
            />
          </label>
          <label>
            Visión
            <textarea
              required
              rows={4}
              value={form.vision}
              onChange={(event) => setForm({ ...form, vision: event.target.value })}
            />
          </label>
          <label>
            Color de acento
            <input
              type="color"
              value={form.accent}
              onChange={(event) => setForm({ ...form, accent: event.target.value })}
            />
          </label>
          <div className="form-actions">
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </button>
            <button className="button button--primary" disabled={submitting} type="submit">
              {submitting ? "Creando…" : "Crear proyecto"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
